// Structured editor for a job's payload_json (the RunJobEvent shape) — replaces
// raw-JSON editing. Fully controlled: reads the payload object from `value` and
// emits an updated object via onChange. No JSON text is ever exposed.
//
// Because no raw JSON is exposed, a field this control does not render is a field
// nobody can set through the UI. The shape lives in payload-model.ts (UI-free, and
// unit-tested) so that contract is checkable.

import { useEffect, useState } from "react";
import {
  BACKEND_OPTIONS,
  BACKEND_OS_EXEC,
  BACKEND_SYSTEMD_TRANSIENT,
  RESTART_POLICY_OPTIONS,
  DEFAULT_RESTART_POLICY,
  isDaemonKind,
  restartPolicyOf,
  setRestartPolicy,
  setRestartDelaySeconds,
  setStopGraceSeconds,
  daemonIssues,
  envIssues,
  formatEnvText,
  parseEnvText,
  setEnv,
  JobPayload,
  backendOf,
  isSystemdTransient,
  runAsIssue,
  runAsSelectOptions,
  setBackend,
  setExecutionUser,
  toPayload,
} from "./payload-model";
import { runAsCandidates } from "./run-as-lookup";
import { fetchDaemonCapability } from "./daemon-capability";

export function PayloadControl(props: {
  value: unknown;
  onChange: (v: JobPayload) => void;
  /** The job's selected worker — the host whose accounts the Run as picker offers. */
  workerUid?: string;
  /** The job's kind. A daemon reveals the restart knobs and pins the backend. */
  kind?: string;
}) {
  const p = toPayload(props.value);
  const command = p.command && p.command.length ? p.command : [""];
  const runDirect = Boolean(p.runDirect);
  const cap = p.captureConsole; // undefined/null = default, true = on, false = off
  const backend = backendOf(p);
  const transient = isSystemdTransient(p);
  const runAsError = runAsIssue(p);
  const daemon = isDaemonKind(props.kind);
  const daemonErrors = daemonIssues(props.kind, p);

  // The env textarea holds its own text. Reformatting the payload back into the box on
  // every keystroke would eat a half-typed line the moment it stopped parsing, so the
  // text is the person's and the payload is what parses out of it. The parsed object is
  // emitted as they type; lines that do not parse are reported, never silently dropped.
  const [envText, setEnvText] = useState(() => formatEnvText(p));
  const envParsed = parseEnvText(envText);
  const envErrors = envIssues(envParsed.badLines);

  // The accounts continuum knows on the job's host. The fetched list is STAMPED with the
  // worker it was fetched for, so switching workers cannot leave the previous host's
  // accounts on offer while the new fetch is in flight — the stale stamp simply does not
  // match, and the picker shows loading instead of the wrong host's users.
  const workerUid = props.workerUid ?? "";
  const [fetched, setFetched] = useState<{ workerUid: string; logins: string[] }>();
  useEffect(() => {
    if (!workerUid) return;
    let live = true;
    runAsCandidates(workerUid)
      // A failed lookup must not block editing: record an empty list for this worker so
      // the picker settles on whatever the job already has. The executor is the authority
      // either way, so a missing suggestion list costs nothing but convenience.
      .catch(() => [] as string[])
      .then((logins) => {
        if (live) setFetched({ workerUid, logins });
      });
    return () => {
      live = false;
    };
  }, [workerUid]);

  // Whether this worker's runner can host a daemon at all. Same stamping rule as the
  // accounts above: an answer belongs to the worker it was fetched for.
  const [capability, setCapability] = useState<{ workerUid: string; capable?: boolean }>();
  useEffect(() => {
    if (!workerUid || !daemon) return;
    let live = true;
    fetchDaemonCapability(workerUid).then((capable) => {
      if (live) setCapability({ workerUid, capable });
    });
    return () => {
      live = false;
    };
  }, [workerUid, daemon]);
  const capableNow = capability?.workerUid === workerUid ? capability.capable : undefined;

  const fresh = fetched?.workerUid === workerUid ? fetched : undefined;
  const candidates = fresh?.logins ?? [];
  const candidatesLoaded = Boolean(fresh) || !workerUid;
  const runAsOptions = runAsSelectOptions(candidates, p.executionUser);
  const currentIsUnknown = runAsOptions.some((o) => !o.known);

  const emit = (patch: Partial<JobPayload>) =>
    props.onChange({ ...p, command, ...patch });

  // The run-as pair goes through the model's helpers rather than a patch, because
  // both have a drop-the-key rule (os-exec writes nothing; a blank user writes nothing)
  // that a spread cannot express.
  const emitPayload = (next: JobPayload) => props.onChange({ ...next, command });

  // Why the picker says what it says, in the order the reader hits the states.
  const runAsHelp = () => {
    if (!transient)
      return `Only systemd transient jobs run as another user; ${BACKEND_OS_EXEC} runs as the worker's own account.`;
    if (!workerUid) return "Choose a worker first — the accounts on offer are that host's.";
    if (currentIsUnknown)
      return "This account is not a worker on the job's host. It was probably set elsewhere, or the worker was removed — the run fails unless the host's executor policy still grants it.";
    if (candidatesLoaded && !candidates.length)
      return "Continuum knows no other accounts on this host. A run-as user has to be an onboarded worker there.";
    return "Accounts continuum knows on this host. The host's executor policy is still the authority — a user it has not been granted fails the run.";
  };

  const setArg = (i: number, val: string) => {
    const next = command.slice();
    next[i] = val;
    emit({ command: next });
  };

  const capValue = cap === true ? "on" : cap === false ? "off" : "default";
  const capLabel: Record<string, string> = { default: "Default", on: "On", off: "Off" };

  return (
    <div className="dfr-payload">
      <div className="dfr-payload-label">Command</div>
      <div className="dfr-argv">
        {command.map((arg, i) => (
          <div className="dfr-arg" key={i}>
            <span className={"idx" + (i === 0 ? " prog" : "")}>
              {i === 0 ? "prog" : "arg " + i}
            </span>
            <input
              className="mono"
              value={arg}
              placeholder={i === 0 ? "program" : "argument"}
              onChange={(e) => setArg(i, e.target.value)}
            />
            <button
              type="button"
              className="x"
              aria-label="remove argument"
              onClick={() => emit({ command: command.filter((_, j) => j !== i) })}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="dfr-add-arg"
        onClick={() => emit({ command: [...command, ""] })}
      >
        + Add argument
      </button>

      <div className="dfr-grid two" style={{ marginTop: 16 }}>
        <div className="dfr-field">
          <label>Working directory</label>
          <input
            className="mono"
            value={p.cwd ?? ""}
            onChange={(e) => emit({ cwd: e.target.value })}
          />
          <div className="dfr-help">Must be readable by the account the job runs as.</div>
        </div>
        <div className="dfr-field">
          <label>Capture console</label>
          <div className="dfr-seg" data-disabled={!runDirect}>
            {(["default", "on", "off"] as const).map((m) => (
              <button
                type="button"
                key={m}
                aria-pressed={capValue === m}
                disabled={!runDirect}
                onClick={() =>
                  emit({ captureConsole: m === "on" ? true : m === "off" ? false : null })
                }
              >
                {capLabel[m]}
              </button>
            ))}
          </div>
          <div className="dfr-help">
            {runDirect
              ? "Streams stdout/stderr to a console log."
              : "Console capture is only available for direct runs."}
          </div>
        </div>
      </div>

      <div className="dfr-grid two" style={{ marginTop: 16 }}>
        <div className="dfr-field">
          <label htmlFor="payload-backend">Launched by</label>
          <select
            id="payload-backend"
            value={daemon ? BACKEND_SYSTEMD_TRANSIENT : backend}
            disabled={daemon}
            onChange={(e) => emitPayload(setBackend(p, e.target.value))}
          >
            {BACKEND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.text}
              </option>
            ))}
          </select>
          <div className="dfr-help">
            {daemon
              ? "A daemon is always launched by the executor — nothing else can hold a unit open across the runner's own restarts."
              : "The systemd option needs a job executor configured on the worker's host; without one the job fails at launch rather than falling back."}
          </div>
        </div>
        <div className={"dfr-field" + (runAsError ? " err" : "")}>
          <label htmlFor="payload-execution-user">Run as</label>
          <select
            id="payload-execution-user"
            className="mono"
            value={p.executionUser ?? ""}
            disabled={!transient || !workerUid}
            onChange={(e) => emitPayload(setExecutionUser(p, e.target.value))}
          >
            <option value="">
              {candidatesLoaded || runAsOptions.length ? "— choose an account —" : "loading…"}
            </option>
            {runAsOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.text}
              </option>
            ))}
          </select>
          {runAsError ? (
            <div className="dfr-field-msg error">{runAsError}</div>
          ) : (
            <div className="dfr-help">{runAsHelp()}</div>
          )}
        </div>
      </div>

      {daemon ? (
        <div className="dfr-grid two" style={{ marginTop: 16 }}>
          <div className="dfr-field">
            <label htmlFor="payload-restart-policy">Restart policy</label>
            <select
              id="payload-restart-policy"
              value={restartPolicyOf(p) || DEFAULT_RESTART_POLICY}
              onChange={(e) => emitPayload(setRestartPolicy(p, e.target.value))}
            >
              {RESTART_POLICY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.text}
                </option>
              ))}
            </select>
            <div className="dfr-help">
              systemd restarts the unit; the runner's convergence loop relaunches it if
              systemd itself gives up.
            </div>
          </div>
          <div className="dfr-field">
            <label htmlFor="payload-restart-delay">Restart delay (seconds)</label>
            <input
              id="payload-restart-delay"
              type="number"
              min={0}
              value={p.restartDelaySeconds ?? ""}
              onChange={(e) => emitPayload(setRestartDelaySeconds(p, e.target.value))}
            />
            <div className="dfr-help">
              How long systemd waits before restarting. Blank leaves systemd's default.
            </div>
          </div>
          <div className="dfr-field">
            <label htmlFor="payload-stop-grace">Stop grace (seconds)</label>
            <input
              id="payload-stop-grace"
              type="number"
              min={0}
              value={p.stopGraceSeconds ?? ""}
              onChange={(e) => emitPayload(setStopGraceSeconds(p, e.target.value))}
            />
            <div className="dfr-help">
              How long a stop or restart waits for the daemon to finish before it is killed.
              Blank leaves systemd's default; the host caps it (120 s on the fleet).
            </div>
          </div>
        </div>
      ) : null}

      {transient ? (
        <div className="dfr-field" style={{ marginTop: 16 }}>
          <label htmlFor="payload-env">Environment</label>
          <textarea
            id="payload-env"
            className="mono"
            rows={5}
            value={envText}
            placeholder={"A8_LOG_LEVEL=debug\nJAVA_OPTS=-Xmx2g"}
            onChange={(e) => {
              setEnvText(e.target.value);
              emitPayload(setEnv(p, parseEnvText(e.target.value).env));
            }}
          />
          {envErrors.length ? (
            envErrors.map((msg) => (
              <div className="dfr-field-msg error" key={msg}>
                {msg}
              </div>
            ))
          ) : (
            <div className="dfr-help">
              One <code>KEY=VALUE</code> per line — this job&rsquo;s own override. The
              execution user&rsquo;s defaults are merged underneath at dispatch, so only what
              is specific to this job belongs here.
            </div>
          )}
        </div>
      ) : null}

      {daemon && capableNow === false ? (
        <div className="dfr-field-msg error" style={{ marginTop: 12 }}>
          The job runner on this host is not daemon-capable — it has no job executor, so a
          daemon job assigned to it is stored and never starts. Choose a host whose runner
          reports daemon capability, or have the executor enabled there.
        </div>
      ) : null}
      {daemonErrors.map((msg) => (
        <div className="dfr-field-msg error" style={{ marginTop: 12 }} key={msg}>
          {msg}
        </div>
      ))}

      <div className="dfr-toggle" style={{ marginTop: 14 }}>
        <button
          type="button"
          className="dfr-sw"
          role="switch"
          aria-checked={runDirect}
          onClick={() => emit({ runDirect: !runDirect })}
        />
        <div>
          <b>Run direct</b>
          <div className="dfr-help">
            Run the command directly, without the <code>a8 worker run</code> wrapper.
          </div>
        </div>
      </div>
    </div>
  );
}
